import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useApi, errMsg, fmtIST, MODE_LABEL } from "@/lib/useApi";

const OUT = {
  correct: "border-emerald-200 bg-emerald-50 text-emerald-700",
  wrong: "border-red-200 bg-red-50 text-red-700",
  unanswered: "border-slate-200 text-slate-600",
  not_evaluated: "border-amber-200 bg-amber-50 text-amber-700",
};

export default function AttemptResult() {
  const { id } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const [a, setA] = useState(null);

  useEffect(() => {
    api.get(`/attempts/${id}`).then((r) => {
      if (r.data.status === "in_progress") return nav(`/exam/${id}`, { replace: true });
      setA(r.data);
    }).catch((e) => toast.error(errMsg(e)));
  }, [id, api, nav]);

  if (!a?.result) return <p className="text-slate-500" data-testid="result-loading">Loading result…</p>;
  const r = a.result;
  const outcome = Object.fromEntries(r.questions.map((x) => [x.qid, x]));

  return (
    <div className="space-y-6 max-w-5xl" data-testid="attempt-result-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{MODE_LABEL[a.mode]} · submitted {fmtIST(a.submitted_at)}{a.submit_reason === "timeout" ? " (auto, time over)" : ""}</p>
          <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight mt-1">Test completed</h1>
          <p className="text-sm text-slate-500 mt-1">{a.label}</p>
        </div>
        <Button variant="outline" asChild data-testid="back-dashboard-btn"><Link to="/app">Back to dashboard</Link></Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Score" value={`${r.score} / ${r.max_score}`} testid="result-score" />
        <Stat label="Percentage" value={`${r.percent}%`} testid="result-percent" />
        <Stat label="Accuracy" value={`${r.accuracy}%`} testid="result-accuracy" />
        <Stat label="Correct · Wrong · Skipped" value={`${r.correct} · ${r.wrong} · ${r.unanswered}`} testid="result-counts" />
      </div>

      <Card className="p-6 border-slate-200">
        <h2 className="font-display font-semibold text-lg">Section-wise performance</h2>
        <div className="mt-4 space-y-4">
          {r.sections.map((s) => (
            <div key={s.name} data-testid={`result-section-${s.name.replace(/\s+/g, "-")}`}>
              <div className="flex justify-between text-sm"><span className="font-medium">Section {s.name}</span><span className="font-mono">{s.score} / {s.max}</span></div>
              <Progress value={s.max ? Math.max(0, (100 * s.score) / s.max) : 0} className="h-2 mt-2" />
              <p className="text-xs text-slate-500 mt-1">{s.correct} correct · {s.wrong} wrong · {s.unanswered} skipped{s.not_evaluated ? ` · ${s.not_evaluated} beyond attempt limit` : ""}</p>
            </div>
          ))}
        </div>
        {a.blueprint && <p className="text-xs text-slate-500 mt-4">Blueprint {a.blueprint.version} · {a.blueprint.verification_status}</p>}
      </Card>

      <div className="space-y-4">
        <h2 className="font-display font-semibold text-lg">Solutions</h2>
        {a.questions.map((q, i) => <ReviewItem key={q.qid} q={q} i={i} o={outcome[q.qid]} />)}
      </div>
    </div>
  );
}

function ReviewItem({ q, i, o }) {
  return (
    <Card className="p-5 border-slate-200" data-testid={`review-q-${i + 1}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Badge variant="outline" className="border-slate-200">Q{i + 1} · Section {q.section}</Badge>
        <Badge variant="outline" className={OUT[o.outcome]}>{o.outcome.replace("_", " ")} · {o.marks > 0 ? "+" : ""}{o.marks}</Badge>
        {q.unit && <span className="text-xs text-slate-500">{q.unit}</span>}
      </div>
      <p className="text-slate-900 whitespace-pre-wrap">{q.stem}</p>
      <div className="mt-3 grid gap-2">
        {q.options.map((op) => {
          const isC = q.correct.includes(op.key), isS = o.selected.includes(op.key);
          return (
            <div key={op.key} className={`px-3 py-2 rounded-md border text-sm ${isC ? "border-emerald-300 bg-emerald-50" : isS ? "border-red-300 bg-red-50" : "border-slate-200"}`}>
              <span className="font-semibold mr-2">{op.key}.</span>{op.text}{isS && <span className="ml-2 text-xs text-slate-500">(your answer)</span>}
            </div>
          );
        })}
      </div>
      {q.explanation && <p className="mt-3 text-sm text-slate-600"><span className="font-medium text-slate-800">Explanation: </span>{q.explanation}</p>}
    </Card>
  );
}

const Stat = ({ label, value, testid }) => (
  <Card className="p-5 border-slate-200" data-testid={testid}>
    <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</p>
    <p className="font-display font-extrabold text-2xl mt-2 text-slate-900">{value}</p>
  </Card>
);
